
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import * as React from 'react';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Stack from '@mui/material/Stack';
import ResponsiveContainer from 'components/atomic/organisms/responsive-container';
import MarkdownViewer from 'components/atomic/organisms/markdown-viewer';
import CourseCard from 'components/atomic/molecules/course-card';
import PathCard from 'components/atomic/molecules/path-card/v2';
import ToolCard from 'pages/wiki-detail/embedded-item/tool';
import RoleCard from 'pages/wiki-detail/embedded-item/role';
import MainLayout from 'components/layouts/main-layout';
import RightPanel from 'pages/wiki-detail/right-panel';
import ItemList from 'pages/wiki-detail/item-list';
import Hero from 'pages/wiki-detail/hero';
import { Language as LanguageType } from 'types/wiki';
import Routes, { toAbsoluteUrl } from 'utils/routes';
import devvSdk from 'utils/devv-sdk';
const SEO = ({ language }: {
    language: LanguageType;
}) => {
    const router = useRouter();
    const keywords = [
        'linguaggio di programmazione',
        'linguaggio di markup',
        'linguaggio di scripting',
        ...language.categoriesTitles,
    ];
    const OGTitle = `${language.title}: scopri tutti i segreti!`;
    const description = `Cos'è ${language.title}? A cosa serve? Scopri tutti i segreti di ${language.title}: 
${language.description}`;
    return (<>
            <NextSeo title={language.title} titleTemplate={`Cos'è %s e a cosa serve? | Devv`} description={description} canonical={`${toAbsoluteUrl(router.asPath)}`} additionalMetaTags={[
            {
                property: 'keywords',
                content: keywords.join(', '),
            },
        ]} openGraph={{
            url: `${toAbsoluteUrl(router.asPath)}`,
            images: [
                {
                    url: Routes.OGImageTitleCTA(OGTitle, 'DEVV WIKI'),
                    secureUrl: Routes.OGImageTitleCTA(OGTitle, 'DEVV WIKI'),
                    type: 'image/png',
                    width: 1200,
                    height: 630,
                },
            ],
        }}/>
            <BreadcrumbJsonLd itemListElements={[
            {
                position: 1,
                name: 'Wiki',
                item: `${toAbsoluteUrl(Routes.Wiki)}`,
            },
            {
                position: 2,
                name: 'Linguaggi di programmazione',
                item: `${toAbsoluteUrl(Routes.WikiLanguages)}`,
            },
            {
                position: 3,
                name: language.title,
                item: `${toAbsoluteUrl(Routes.WikiLanguage(language.slug))}`,
            },
        ]}/>
        </>);
};
interface LanguagePageProps {
    language: LanguageType;
}
const Language: NextPage<LanguagePageProps> = ({ language }) => {
    return (<>
            <SEO language={language}/>

            <MainLayout isFluid>
                <Hero title={language.title} description={language.description} logo={language.logo} categoriesTitles={language.categoriesTitles} color="#990038" breadcrumbs={[
            {
                title: 'Wiki',
                href: undefined,
            },
            {
                title: 'Linguaggi',
                href: Routes.WikiLanguages,
            },
            {
                title: language.title,
                href: undefined,
            },
        ]}/>

                <ResponsiveContainer maxWidth={'lg'}>
                    <Stack direction={'row'} paddingY={2}>
                        <Stack flexGrow={1}>
                            <MarkdownViewer>{language.body}</MarkdownViewer>

                            {language.courses.length ? (<ItemList title={`Corsi di ${language.title}`} items={language.courses} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} mt={4} renderItem={(item) => <CourseCard {...item}/>}/>) : null}

                            {language.paths.length ? (<ItemList title={`Percorsi di ${language.title}`} items={language.paths} mt={4} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} renderItem={(item) => <PathCard {...item}/>}/>) : null}

                            {language.tools.length ? (<ItemList title={`Strumenti per ${language.title}`} items={language.tools} mt={4} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} renderItem={(item) => <ToolCard item={item}/>}/>) : null}

                            {language.roles.length ? (<ItemList title={`Le professioni in cui viene usato ${language.title}`} items={language.roles} mt={4} itemColumns={{ xs: 12, sm: 6, md: 4, lg: 6 }} renderItem={(item) => <RoleCard item={item}/>}/>) : null}
                        </Stack>

                        <RightPanel />
                    </Stack>
                </ResponsiveContainer>
            </MainLayout>
        </>);
};
export const getStaticPaths: GetStaticPaths = async () => {
    const { data } = await devvSdk.getLanguagesOverview();
    return {
        paths: data.map((el) => ({
            params: { slug: el.slug },
        })),
        fallback: false,
    };
};
const getStaticProps: GetStaticProps<LanguagePageProps> = async ({ params }) => {
    try {
        if (!params?.slug)
            throw 'Missing params or slug';
        const { data: language } = await devvSdk.getLanguageBySlug(params.slug as string);
        if (!language)
            throw 'No language found';
        return {
            props: {
                language,
            },
        };
    }
    catch (error) {
        return {
            redirect: {
                destination: Routes.WikiLanguages,
                permanent: false,
            },
        };
    }
};
export default Language;

    async function __Next_Translate__getStaticProps__18916ceb2c2__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/wiki/languages/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__18916ceb2c2__ as getStaticProps }
  